import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";

import {
    FormDateRangePicker,
    FormDropDownList,
    FormAutoComplete,
    FormCheckbox,
} from "../../forms/form-components";

export const AdministratorVenueEventStatusChanges = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "All Websites",
    };
    const defaultVenueEventStatusType = {
        venueEventStatusTypeId: "",
        name: "All Statuses",
    };

    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [venueEventStatusTypes, setVenueEventStatusTypes] = useState([]);

    const [venueEventStatusChanges, setVenueEventStatusChanges] = useState(undefined);
    const [venueEventStatusChangeCount, setVenueEventStatusChangeCount] = useState(0);
    const [skipCount, setSkipCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(1);
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(undefined);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [dataLoadingError, setDataLoadingError] = useState(undefined);
    const [searchCriteria, setSearchCriteria] = useState(undefined);

    const autocompleteTimeout = useRef();
    const autocompleteTimeoutDelay = 500; //Milliseconds

    const getTicketingWebsites = () => {
        const dataItem = {
            onlyActive: true,
        }

        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(allTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const getVenueEventStatusTypes = () => {
        fetch(`/api/general/venueeventstatustypes`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allVenueEventStatusTypes = [defaultVenueEventStatusType].concat(result);
                setVenueEventStatusTypes(allVenueEventStatusTypes);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }
        
        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);  
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, autocompleteTimeoutDelay);

        setAreVenuesLoading(true);
    };

    const getVenueEventStatusChanges = (dataItem, newPageNumber) => {
        setIsDataLoading(true);

        if (!dataItem) {
            dataItem = searchCriteria;
        }
        
        dataItem.pageNumber = newPageNumber;
    
        fetch(`/api/administrator/report/venueeventstatuschanges`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setIsDataLoading(false);                
                setItemsPerPage(result.venueEventStatusChangesPerPage);
                setSkipCount(result.venueEventStatusChangesPerPage * (result.pageNumber - 1));
                setVenueEventStatusChangeCount(result.totalVenueEventStatusChangeCount);                
                setVenueEventStatusChanges(result.venueEventStatusChanges);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsDataLoading(false);
                setDataLoadingError(error.message);
            });
    };

    const getData = (dataItem) => {
        if (dataItem.groupByEvent === undefined) {
            dataItem.groupByEvent = true;
        }

        if (dataItem.ticketingWebsite !== undefined
            && dataItem.ticketingWebsite.value !== "") {
            dataItem.ticketingWebsiteId = parseInt(dataItem.ticketingWebsite.ticketingWebsiteId);
        }
        else {
            dataItem.ticketingWebsiteId = undefined;
        }

        if (dataItem.currentVenueEventStatusType !== undefined
            && dataItem.currentVenueEventStatusType.value !== "") {
            dataItem.currentVenueEventStatusTypeId = parseInt(dataItem.currentVenueEventStatusType.venueEventStatusTypeId);
        }
        else {
            dataItem.currentVenueEventStatusTypeId = undefined;
        }

        if (dataItem.oldVenueEventStatusType !== undefined
            && dataItem.oldVenueEventStatusType.value !== "") {
            dataItem.oldVenueEventStatusTypeId = parseInt(dataItem.oldVenueEventStatusType.venueEventStatusTypeId);
        }
        else {
            dataItem.oldVenueEventStatusTypeId = undefined;
        }

        if (dataItem.newVenueEventStatusType !== undefined
            && dataItem.newVenueEventStatusType.value !== "") {
            dataItem.newVenueEventStatusTypeId = parseInt(dataItem.newVenueEventStatusType.venueEventStatusTypeId);
        }
        else {
            dataItem.newVenueEventStatusTypeId = undefined;
        }

        dataItem.venueKey = undefined;
        if (venues.length > 0) {
            const venue = venues.find(element => element.currentName === venueName);

            if (venue) {
                dataItem.venueKey = venue.venueKey;
            }
        }

        dataItem.startingChangeDate = undefined;
        dataItem.endingChangeDate = undefined;
        if (dataItem.changeDates) {
            if (dataItem.changeDates.start) {
                dataItem.startingChangeDate = dataItem.changeDates.start;
            }

            if (dataItem.changeDates.end) {
                dataItem.endingChangeDate = dataItem.changeDates.end;
            }
        }

        dataItem.startingEventDate = undefined;
        dataItem.endingEventDate = undefined;
        if (dataItem.eventDates) {
            if (dataItem.eventDates.start) {
                dataItem.startingEventDate = dataItem.eventDates.start;
            }

            if (dataItem.eventDates.end) {
                dataItem.endingEventDate = dataItem.eventDates.end;
            }
        }
        
        setSearchCriteria(dataItem);
        getVenueEventStatusChanges(dataItem);
    }

    const pageChange = (event) => {
        const newPageNumber = (event.page.skip / event.page.take) + 1;
        getVenueEventStatusChanges(null, newPageNumber);
    };

    const ViewUrlCell = (props) => {  
        const url = props.dataItem[props.field];     

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    useEffect(() => {
        document.title = "Event Status Changes";

        getTicketingWebsites();
        getVenueEventStatusTypes();
    }, []);

    return (
        <div className="administratorReport">
            <Form
                onSubmit={getData}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">      
                                <div className="col-12">
                                    <Field
                                        id={"groupByEvent"}
                                        name={"groupByEvent"}
                                        label={"Group by Event"}
                                        component={FormCheckbox}
                                        defaultValue={true}
                                    />
                                </div>                                
                                <div className="col-md-4">
                                    <Field
                                        id={"ticketingWebsite"}
                                        name={"ticketingWebsite"}
                                        label={"Ticketing website"}
                                        component={FormDropDownList}
                                        data={ticketingWebsites}
                                        textField="name"
                                        dataItemKey="ticketingWebsiteId"
                                        defaultValue={ticketingWebsites.find(element => element.ticketingWebsiteId === "")}
                                    /> 
                                </div>
                                <div className="col-md-8">
                                    <Field
                                        id={"venueName"}
                                        name={"venueName"}
                                        label={"Venue"}
                                        component={FormAutoComplete}
                                        data={venues}        
                                        placeholder="e.g. Roseland"
                                        textField="currentName"
                                        onChange={venueChanged}
                                        loading={areVenuesLoading}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"currentVenueEventStatusType"}
                                        name={"currentVenueEventStatusType"}
                                        label={"Current event status"}
                                        component={FormDropDownList}
                                        data={venueEventStatusTypes}
                                        textField="name"
                                        dataItemKey="venueEventStatusTypeId"
                                        defaultValue={venueEventStatusTypes.find(element => element.venueEventStatusTypeId === "")}
                                    /> 
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"oldVenueEventStatusType"}
                                        name={"oldVenueEventStatusType"}
                                        label={"Old event status"}
                                        component={FormDropDownList}
                                        data={venueEventStatusTypes}
                                        textField="name"
                                        dataItemKey="venueEventStatusTypeId"
                                        defaultValue={venueEventStatusTypes.find(element => element.venueEventStatusTypeId === "")}
                                    /> 
                                </div>
                                <div className="col-md-4">
                                    <Field
                                        id={"newVenueEventStatusType"}
                                        name={"newVenueEventStatusType"}
                                        label={"New event status"}
                                        component={FormDropDownList}
                                        data={venueEventStatusTypes}
                                        textField="name"
                                        dataItemKey="venueEventStatusTypeId"
                                        defaultValue={venueEventStatusTypes.find(element => element.venueEventStatusTypeId === "")}
                                    /> 
                                </div>
                                <div className="col-md-6 col-lg-5">
                                    <Field
                                        id={"changeDates"}
                                        name={"changeDates"}
                                        component={FormDateRangePicker}
                                        format={"MMM dd, yyyy"}
                                        startDateInputSettings={{
                                            label: "Starting change date"
                                        }}
                                        endDateInputSettings={{
                                            label: "Ending change date"
                                        }}                                        
                                        calendarSettings={{
                                            views: 1
                                        }}
                                    />
                                </div>  
                                <div className="col-md-6 col-lg-5">
                                    <Field
                                        id={"eventDates"}
                                        name={"eventDates"}
                                        component={FormDateRangePicker}
                                        format={"MMM dd, yyyy"}
                                        startDateInputSettings={{
                                            label: "Starting event date"
                                        }}
                                        endDateInputSettings={{
                                            label: "Ending event date"
                                        }}                                        
                                        calendarSettings={{
                                            views: 1
                                        }}
                                    />
                                </div> 
                                <div className="col-lg-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="search"                                            
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>                        
                        </fieldset>
                    </FormElement>
                )}
            />

            { isDataLoading && <LoadingPanel /> }

            { dataLoadingError && 
                <div>
                    {dataLoadingError}
                </div>
            }

            { venueEventStatusChanges &&
                <Grid
                    data={venueEventStatusChanges}
                    resizable={true}
                    skip={skipCount}
                    take={itemsPerPage}
                    total={venueEventStatusChangeCount}
                    pageable={true}
                    onPageChange={pageChange}
                    scrollable={"none"}
                >      
                    <Column field="dateCreated" title={"Created"} />
                    <Column field="eventName" title={"Event"} />
                    <Column field="venueName" title={"Venue"} />
                    <Column field="eventDate" title={"Event Date"} />
                    <Column field="currentVenueEventStatusTypeName" title={"Current Status"} />
                    <Column field="oldVenueEventStatusTypeName" title={"Old Status"} />
                    <Column field="newVenueEventStatusTypeName" title={"New Status"} /> 
                    <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />  
                </Grid>
            }
        </div>
    );
}